import { Icon, IconButton, Stack, Title } from "@introist/react-foundation/v2";
import { ListView, useSearch } from "../../../foundation";
import { ListRow } from "../../../components/organisms";
import { formatDateTime } from "../../../utils/DatesUtils";
import { DateFormats } from "../../../utils/dates";
import { TaskAssignment, useTaskAssignments } from "../useTaskAssignments";
import { api } from "../../../services/rpc/RpcProvider";

const AssignmentStatus = ({ assignment }: { assignment: TaskAssignment }) => {
  if (assignment.completedAt) {
    return (
      <Stack gap="xSmall">
        <Icon name="check" />
        <Title variant="bold">
          Completed {formatDateTime(assignment.completedAt, DateFormats.datetime.withDay.short)}
        </Title>
      </Stack>
    );
  }
  return <Title>Pending</Title>;
};

export const TaskAssignments = ({ taskId }: { taskId: string }) => {
  const { assignments } = useTaskAssignments(taskId);
  const { data: employees } = api.employees.v4.list.useQuery({});

  const { filtered, search, setSearch } = useSearch(assignments, {
    fields: ["employeeId"]
  });

  const getEmployeeName = (employeeId: string | null) => {
    if (!employeeId) return null;
    return employees?.find(e => e.id === employeeId)?.name ?? null;
  };

  return (
    <Stack vertical>
      <ListView.Header
        title="Assignments"
        count={filtered?.length}
        search={{ search, setSearch }}
      />
      <ListView.List
        data={filtered}
        empty={{
          icon: "check",
          title: "No assignments yet",
          message: "Assignments are created when this task is delivered via an automation step."
        }}
        renderRow={assignment => {
          const employeeName = getEmployeeName(assignment.employeeId);
          const canNavigate = !!(assignment.automationId && assignment.automationStepId);

          return (
            <ListRow
              key={assignment.id}
              gridTemplateColumns="140px 1fr auto"
              clickable={canNavigate}
              onClick={
                canNavigate
                  ? () =>
                      window.open(
                        `/automations/${assignment.automationId}/steps/${assignment.automationStepId}`,
                        "_blank"
                      )
                  : undefined
              }
            >
              <Title>{formatDateTime(assignment.assignedAt, DateFormats.datetime.withDay.short)}</Title>
              <Stack gap="xSmall">
                <Title variant="bold">{employeeName ?? "Unknown"}</Title>
                {assignment.employeeId && (
                  <IconButton
                    size="small"
                    variant="blended"
                    icon="moveTo"
                    dimmed
                    onClick={e => {
                      e.stopPropagation();
                      window.open(`/employees/${assignment.employeeId}`, "_blank");
                    }}
                  />
                )}
              </Stack>
              <AssignmentStatus assignment={assignment} />
            </ListRow>
          );
        }}
      />
    </Stack>
  );
};
